import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

const btnStyle = (active) => ({
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  background: active ? "#e0f2fe" : "#fff",
  color: active ? "#0369a1" : "#334155",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
});

function Toolbar({ editor }) {
  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        padding: "8px 0",
        borderBottom: "1px solid #e2e8f0",
        marginBottom: 8,
      }}
    >
      <button type="button" style={btnStyle(editor.isActive("heading", { level: 2 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        H2
      </button>
      <button type="button" style={btnStyle(editor.isActive("heading", { level: 3 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        H3
      </button>
      <button type="button" style={btnStyle(editor.isActive("bold"))} onClick={() => editor.chain().focus().toggleBold().run()}>
        Bold
      </button>
      <button type="button" style={btnStyle(editor.isActive("italic"))} onClick={() => editor.chain().focus().toggleItalic().run()}>
        Italic
      </button>
      <button type="button" style={btnStyle(editor.isActive("bulletList"))} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        • List
      </button>
      <button type="button" style={btnStyle(editor.isActive("orderedList"))} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        1. List
      </button>
      <button type="button" style={btnStyle(editor.isActive("link"))} onClick={setLink}>
        Link
      </button>
    </div>
  );
}

function isEmptyHtml(html) {
  const plain = String(html || "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return plain === "";
}

export default function RichTextEditor({ content, value, onChange, placeholder = "Write your article…" }) {
  const incoming = content ?? value ?? "";
  const lastEmittedRef = useRef(incoming);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: incoming || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastEmittedRef.current = html;
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: "cool-article-editor",
        style:
          "min-height:220px;padding:12px 14px;outline:none;font-size:15px;line-height:1.6;color:#0f172a;",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (incoming === lastEmittedRef.current) return;
    if (isEmptyHtml(incoming) && isEmptyHtml(editor.getHTML())) {
      lastEmittedRef.current = incoming;
      return;
    }
    editor.commands.setContent(incoming || "", { emitUpdate: false });
    lastEmittedRef.current = incoming;
  }, [editor, incoming]);

  return (
    <div
      style={{
        border: "1px solid rgba(148,163,184,0.9)",
        borderRadius: 12,
        background: "rgba(248,250,252,0.95)",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "0 10px" }}>
        <Toolbar editor={editor} />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
